
import type { DailySessionData } from '@empathybroker/tsclient-stats/dist/models/daily-session/daily-session-data'
import type { DailySessionResponse } from '@empathybroker/tsclient-stats/dist/models/daily-session/daily-session.response'
import type { PropType } from 'vue'
import type { CompareFilterEnum } from '../../../enums'
import type { Dictionary, PeriodsData, RegularFilter } from '../../../models'
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer'
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'
import { useKpis } from '../../../composables/use-kpis'
import { AlignEnum, FilterIdEnum, KpiEnum, PositionEnum } from '../../../enums'
import { ANALYTICS_CONSTS } from '../../../store/analytics/analytics.consts'
import { CONTEXT_CONSTS } from '../../../store/context/context.consts'
import KpiExpander from './kpi-expander.component.vue'
import KpiSummary from './kpi-summary.component.vue'

export enum AnimationStepsIndexEnum {
  EXPANDER = 0,
  KPIS = 1,
}

export interface DistData {
  queryCount?: number
  clickCountPct?: number
  findability?: number
  add2cartCountPct?: number
  checkoutCountPct?: number
  noResultsCountPct?: number
  sessionCount?: number
  queriesPerSession?: number
}

export interface Data {
  dist?: DistData
  sessions?: DailySessionData
}

type FunnelChartData = Partial<Record<KpiEnum, number[]>>

export default defineComponent({
  name: 'FunnelChart',
  components: {
    KpiExpander,
    KpiSummary,
  },
  props: {
    selectedId: String,
    animationStepDurationsInMillis: Array as PropType<Array<number>>,
    currentAnimationStep: Number,
    data: Object as PropType<{ dist: DistData; sessions: DailySessionData }>,
    height: {
      type: Number,
      default: 100,
    },
    kpis: {
      type: Array as PropType<Array<KpiEnum>>,
      required: true,
    },
    showAsides: Boolean,
  },
  setup() {
    const { findabilityEngagementKpi } = useKpis()
    return {
      findabilityEngagementKpi,
    }
  },
  computed: {
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      filters: CONTEXT_CONSTS.getters.enabledFilters,
    }),
    ...mapGetters(ANALYTICS_CONSTS.moduleName, {
      analyticsData: ANALYTICS_CONSTS.getters.data,
    }),
    funnelChartKpis(): KpiEnum[] {
      const funnelChartSupportedKpis = [
        KpiEnum.CLICKS,
        KpiEnum.FINDABILITY,
        KpiEnum.ADD2CART,
        KpiEnum.CHECKOUT,
        KpiEnum.NO_RESULTS,
      ]
      return this.kpis.filter(kpi => funnelChartSupportedKpis.includes(kpi))
    },
    analyticsDistData(): DistData {
      return !this.selectedId
        ? this.analyticsData.overall.dist
        : this.analyticsData.ids[this.selectedId]?.dist
    },
    analyticsSessionsData(): DistData {
      return !this.selectedId ? this.analyticsData.overall.sessions : undefined
    },
    analyticsFunnelChartData(): { current: Data; compared: Data | undefined } {
      const current: Data = {
        dist: this.getDistData(this.analyticsDistData),
        sessions: this.getSessionData(this.analyticsSessionsData),
      }
      const compared: Data = this.compareFilterValue
        ? {
            dist: this.getDistData(this.analyticsDistData, this.compareFilterValue),
            sessions: this.getSessionData(this.analyticsSessionsData, this.compareFilterValue),
          }
        : {}
      return { current, compared }
    },
    funnelChartData(): FunnelChartData {
      const chartData: FunnelChartData = {}
      this.funnelChartKpis.forEach((kpi: KpiEnum) => {
        chartData[kpi] = this.getKpiChartData(kpi)
      })
      return chartData
    },
    compareFilterValue(): CompareFilterEnum {
      let compareFilterValue: CompareFilterEnum
      const compareFilter: RegularFilter = this.filters[FilterIdEnum.COMPARE]
      if (compareFilter) {
        compareFilterValue = compareFilter.selectedValues[0] as CompareFilterEnum
      }
      return compareFilterValue
    },
    kpiWidth(): number {
      const kpiSummaryWidthPercentage = this.kpiSummary.widthPercentage / 100
      return (
        (this.funnelContainerWidth -
          (this.isKpiExpanderDisplayed ? this.expander.width : 0) -
          this.groupBackgroundWidth) /
        (this.funnelChartKpis.length + kpiSummaryWidthPercentage / 2)
      )
    },
    groupBackgroundWidth(): number {
      const lastItem: KpiEnum = this.funnelChartKpis.at(-1)
      const isLastGrouped = this.isGroupedKpi(lastItem, this.funnelChartKpis.length - 1)
      return isLastGrouped ? 10 : 0
    },
    kpiTransitionDurationInMillis(): number {
      return (
        this.animationStepDurationsInMillis[AnimationStepsIndexEnum.KPIS] /
        this.funnelChartKpis.length
      )
    },
    kpiTransitionWidthDurationInMillis(): number {
      return (this.kpiTransitionDurationInMillis / 5) * 3
    },
    kpisRowStyles(): Dictionary<string> {
      return {
        marginRight: '45px',
        paddingLeft:
          this.isKpiExpanderDisplayed && this.showAsides ? `${this.expander.width / 2}px` : '0px',
      }
    },
    contentWrapperStyles(): Dictionary<string> {
      return {
        height: `${this.height}px`,
        transitionProperty: 'height',
        transitionDuration: `${
          this.animationStepDurationsInMillis[AnimationStepsIndexEnum.EXPANDER]
        }ms`,
        transitionDelay: `${this.expander.transitionDurationInMillis}ms`,
      }
    },
    kpiExpanderContainerStyles(): Dictionary<string> {
      return {
        transitionProperty: 'margin-left',
        transitionDuration: `${
          this.animationStepDurationsInMillis[AnimationStepsIndexEnum.EXPANDER]
        }ms`,
        transitionDelay: `${this.expander.transitionDurationInMillis}ms`,
        alignItems: AlignEnum.CENTER,
        marginLeft: `${-this.expander.width / 2}px`,
      }
    },
    kpiExpanderStyles(): Dictionary<string> {
      return {
        width: `${this.expander.width}px`,
        height: `${this.height}px`,
        transform: `translate3d(50%, 0, 0)`,
        transitionProperty: 'transform',
        transitionDuration: `${
          this.animationStepDurationsInMillis[AnimationStepsIndexEnum.EXPANDER]
        }ms`,
        transitionDelay: `${this.expander.transitionDurationInMillis}ms`,
      }
    },
    isKpiExpanderDisplayed(): boolean {
      return this.kpis.includes(KpiEnum.QUERIES)
    },
    kpiExpanderConfig(): Record<string, string | object | boolean> {
      return {
        id: KpiEnum.QUERIES,
        core: {
          label: this.getKpiStatsLabel(KpiEnum.QUERIES),
          values: this.getKpiChartData(KpiEnum.QUERIES),
        },
        asides: !this.showAsides
          ? []
          : [
              {
                orientation: 'left',
                label: this.getKpiStatsLabel(KpiEnum.SESSIONS),
                values: this.getKpiChartData(KpiEnum.SESSIONS),
              },
              {
                orientation: 'right',
                label: this.getKpiStatsLabel(KpiEnum.QUERIES_PER_SESSION),
                values: this.getKpiChartData(KpiEnum.QUERIES_PER_SESSION),
              },
            ],
      }
    },
    expander(): Dictionary<number | boolean> {
      return {
        width: this.showAsides ? 350 : 100,
        transitionDurationInMillis: 500,
      }
    },
    kpiSummary(): Dictionary<number | object> {
      return {
        widthPercentage: this.funnelChartKpis.length * 12,
        core: {
          radius: 5,
          padding: 5,
        },
      }
    },
    kpisDisplayed(): Array<KpiEnum> {
      return this.funnelChartKpis.slice(0, this.lastKpiIndexDisplayed + 1)
    },
  },
  data() {
    return {
      lastKpiIndexDisplayed: 0,
      funnelContainerWidth: 0,
      path: {
        strokeWidth: 1,
        height: 90,
      },
      resizeObserver: undefined,
      messages: this.$epb.messages.insights.analytics,
      AnimationStepsIndexEnum,
      KpiEnum,
      PositionEnum,
    }
  },
  mounted(): void {
    const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill
    this.resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const { width } = entry.contentRect
        this.funnelContainerWidth = width
      })
    })
    this.resizeObserver.observe(this.$refs.funnelContainer)
  },
  beforeDestroy(): void {
    this.resizeObserver.disconnect()
  },
  methods: {
    setLastIndexDisplayed(update: number): void {
      this.lastKpiIndexDisplayed += update
    },
    getKpiStyles(): Dictionary<string> {
      return {
        transitionDuration: `${this.kpiTransitionWidthDurationInMillis}ms`,
        width: `${this.kpiWidth}px`,
      }
    },
    getKpiPath(kpiIndex: number, kpi: KpiEnum): string {
      if (kpi === KpiEnum.NO_RESULTS || (!this.isKpiExpanderDisplayed && kpiIndex === 0)) {
        return ''
      }
      const strokeWidth = this.path.strokeWidth / 2

      const kpiWidthForFirstElement =
        this.isKpiExpanderDisplayed && !this.showAsides ? this.expander.width / 2 : 0

      const widthKpi =
        kpiIndex === 0
          ? kpiWidthForFirstElement
          : this.kpiSummary.core.radius + this.kpiSummary.core.padding
      const widthNextKpi = this.kpiSummary.core.radius + this.kpiSummary.core.padding

      return this.getLinealKpiPath(strokeWidth, widthKpi, widthNextKpi)
    },
    getLinealKpiPath(strokeWidth: number, widthKpi: number, widthNextKpi: number) {
      const pathEnd = this.kpiWidth - widthNextKpi
      return `M ${widthKpi} ${strokeWidth} L ${pathEnd} ${strokeWidth}`
    },
    groupBackgroundStyles(): Dictionary<string> {
      const kpiSummaryWidth = this.kpiWidth * (this.kpiSummary.widthPercentage / 100)
      const backgroundPaddingInPixels = 3

      const rowRadiusStyles = this.getRadiusBackgroundStyles(
        kpiSummaryWidth,
        backgroundPaddingInPixels,
      )

      return {
        left: `${-kpiSummaryWidth - backgroundPaddingInPixels}px`,
        width: `${this.kpiWidth + kpiSummaryWidth * 2 + backgroundPaddingInPixels * 2}px`,
        height: '50px',
        transitionDuration: `${this.kpiTransitionWidthDurationInMillis}ms`,
        bottom: `${this.height - 50}px`,
        ...rowRadiusStyles,
      }
    },
    getRadiusBackgroundStyles(
      kpiSummaryWidth: number,
      backgroundPaddingInPixels: number,
    ): Dictionary<string> {
      return {
        borderTopLeftRadius: `${kpiSummaryWidth + backgroundPaddingInPixels}px`,
        borderTopRightRadius: `${kpiSummaryWidth + backgroundPaddingInPixels}px`,
      }
    },
    getDistData(dist: PeriodsData, compareFilterValue?: CompareFilterEnum): DistData {
      if (this.data) {
        return this.data.dist
      }
      if (dist) {
        const periodDistData = compareFilterValue ? dist[compareFilterValue] : dist.currentPeriod
        return periodDistData?.data
      }
      return undefined
    },
    getSessionData(
      sessions: PeriodsData<DailySessionResponse>,
      compareFilterValue?: CompareFilterEnum,
    ): DailySessionData {
      if (this.data) {
        return this.data.sessions
      }
      if (sessions) {
        const periodSessionsData = compareFilterValue
          ? sessions[compareFilterValue]
          : sessions.currentPeriod
        return periodSessionsData?.data
      }
      return undefined
    },
    getKpiChartData(kpi: KpiEnum): number[] {
      const { current, compared } = this.analyticsFunnelChartData
      const kpiData: number = this.getKpiStatsData(kpi, current)
      if (kpi === KpiEnum.QUERIES) {
        this.$emit('queries', kpiData)
      }
      const compareKpiData: number | undefined = this.compareFilterValue
        ? this.getKpiStatsData(kpi, compared)
        : undefined

      return [kpiData, compareKpiData].filter(value => value !== undefined)
    },
    getKpiStatsData(kpi: KpiEnum, { dist, sessions }: Data): number {
      return {
        [KpiEnum.QUERIES]: () => (dist ? dist.queryCount : undefined),
        [KpiEnum.CLICKS]: () => (dist ? dist.clickCountPct : undefined),
        [KpiEnum.FINDABILITY]: () => (dist ? dist.findability : undefined),
        [KpiEnum.ADD2CART]: () => (dist ? dist.add2cartCountPct : undefined),
        [KpiEnum.CHECKOUT]: () => (dist ? dist.checkoutCountPct : undefined),
        [KpiEnum.NO_RESULTS]: () => (dist ? dist.noResultsCountPct : undefined),
        [KpiEnum.SESSIONS]: () => (sessions ? sessions.sessionCount : undefined),
        [KpiEnum.QUERIES_PER_SESSION]: () => (sessions ? sessions.queriesPerSession : undefined),
      }[kpi]()
    },
    getKpiStatsLabel(kpi: KpiEnum): string {
      const kpisMessages = this.messages.kpis
      return {
        [KpiEnum.QUERIES]: () => kpisMessages[KpiEnum.QUERIES].valueLabel,
        [KpiEnum.CLICKS]: () => kpisMessages[KpiEnum.CLICKS].percentageLabel,
        [KpiEnum.FINDABILITY]: () => kpisMessages[KpiEnum.FINDABILITY].percentageLabel,
        [KpiEnum.ADD2CART]: () => kpisMessages[KpiEnum.ADD2CART].percentageLabel,
        [KpiEnum.CHECKOUT]: () => kpisMessages[KpiEnum.CHECKOUT].percentageLabel,
        [KpiEnum.NO_RESULTS]: () => kpisMessages.noResults.valueLabel,
        [KpiEnum.SESSIONS]: () => kpisMessages[KpiEnum.SESSIONS].valueLabel,
        [KpiEnum.QUERIES_PER_SESSION]: () => kpisMessages[KpiEnum.QUERIES_PER_SESSION].valueLabel,
      }[kpi]()
    },
    getKpiInfoStyles() {
      return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: `${this.kpiSummary.widthPercentage}%`,
        height: '100%',
        right: `${-this.kpiSummary.widthPercentage / 2}%`,
        animationDuration: `${
          this.kpiTransitionDurationInMillis - this.kpiTransitionWidthDurationInMillis
        }ms`,
        animationDelay: `${
          this.kpiTransitionDurationInMillis - this.kpiTransitionWidthDurationInMillis
        }ms`,
      }
    },

    isLastKpiOfRow(kpiIndex: number): boolean {
      return kpiIndex === this.funnelChartKpis.length - 1
    },
    isGroupedKpi(kpi: KpiEnum, index: number): boolean {
      const lastKpi: KpiEnum = this.funnelChartKpis[index - 1]
      return !!lastKpi && kpi === KpiEnum.FINDABILITY && lastKpi === this.findabilityEngagementKpi
    },
  },
})
